import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { AdminLayout } from "./components/admin/AdminLayout";
import { Toaster } from "./components/ui/sonner";

// Airline Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import FlightsPage from "./pages/FlightsPage";
import FleetPage from "./pages/FleetPage";
import CrewPage from "./pages/CrewPage";
import DispatchPage from "./pages/DispatchPage";
import MaintenancePage from "./pages/MaintenancePage";
import DeferralsPage from "./pages/DeferralsPage";
import LogbookPage from "./pages/LogbookPage";
import StatisticsPage from "./pages/StatisticsPage";
import CCODashboardPage from "./pages/CCODashboardPage";
import DocumentsPage from "./pages/DocumentsPage";
import SchedulePage from "./pages/SchedulePage";
import CounterPage from "./pages/CounterPage";
import VentasPage from "./pages/VentasPage";
import OpMessagesPage from "./pages/OpMessagesPage";
import RampPage from "./pages/RampPage";
import TurnaroundPage from "./pages/TurnaroundPage";
import TurnaroundFlightPage from "./pages/TurnaroundFlightPage";
import MyFlightsPage from "./pages/MyFlightsPage";
import MyLogbookPage from "./pages/MyLogbookPage";

const CREW_ROLES = ['pilot', 'cabin_crew'];
const CREW_OPEN_PATHS = ['/notifications', '/settings'];
import CrewCalendarPage from "./pages/CrewCalendarPage";
import SMSPage from "./pages/SMSPage";
import ReportsPage from "./pages/ReportsPage";
import BillingPage from "./pages/BillingPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import RevenuePage from "./pages/RevenuePage";
import AIAgentsPage from "./pages/AIAgentsPage";
import GHDashboardPage from "./pages/GHDashboardPage";
import PredictiveMaintenancePage from "./pages/PredictiveMaintenancePage";
import SupportPage from "./pages/SupportPage";
import UsersPage from "./pages/UsersPage";

// Landing Page
import LandingPage from "./pages/LandingPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminTenantsPage from "./pages/admin/AdminTenantsPage";
import AdminLeadsPage from "./pages/admin/AdminLeadsPage";
import AdminBillingPage from "./pages/admin/AdminBillingPage";
import AdminActivityPage from "./pages/admin/AdminActivityPage";
import AdminStaffPage from "./pages/admin/AdminStaffPage";
import AdminAIPage from "./pages/admin/AdminAIPage";
import AdminSecurityPage from "./pages/admin/AdminSecurityPage";
import AdminSupportPage from "./pages/admin/AdminSupportPage";
import { ModuleLockedScreen } from "./components/ModuleUpsell";
import { CookieBanner } from "./components/CookieBanner";
import OnboardingPage from "./pages/OnboardingPage";
import LegalPage from "./pages/LegalPage";
import AdminErrorsPage from "./pages/admin/AdminErrorsPage";

// Protected Route for Airline Users
// Auditoria 3 (post-E2E): soporte para 'requireModule' — bloquea acceso por URL directa
// cuando el modulo no esta habilitado para el tenant.
const ProtectedRoute = ({ children, allowedRoles, requireModule }) => {
    const { isAuthenticated, loading, user, hasModule } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Cargando...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        // Redirect GH users to their dashboard
        if (user?.role === 'gh_supervisor' || user?.role === 'gh_leader') {
            return <Navigate to="/gh" replace />;
        }
        if (CREW_ROLES.includes(user?.role)) return <Navigate to="/mis-vuelos" replace />;
        return <Navigate to="/dashboard" replace />;
    }
    if (!allowedRoles && CREW_ROLES.includes(user?.role) && !CREW_OPEN_PATHS.includes(window.location.pathname)) {
        return <Navigate to="/mis-vuelos" replace />;
    }

    if (requireModule && hasModule && !hasModule(requireModule)) {
        return (
            <DashboardLayout>
                <ModuleLockedScreen moduleKey={requireModule} />
            </DashboardLayout>
        );
    }

    return <DashboardLayout>{children}</DashboardLayout>;
};

// Protected Route for Admin Users
const AdminRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAdminAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Cargando panel...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

    return <AdminLayout>{children}</AdminLayout>;
};

// Admin Login Wrapper - redirects if already authenticated
const AdminLoginWrapper = () => {
    const { isAuthenticated, loading } = useAdminAuth();
    if (loading) return null;
    if (isAuthenticated) return <Navigate to="/admin/dashboard" replace />;
    return <AdminLoginPage />;
};

// Airline Login Wrapper
const AirlineLoginWrapper = () => {
    const { isAuthenticated, loading, user } = useAuth();
    if (loading) return null;
    if (isAuthenticated) {
        // Redirect GH users to /gh, others to /dashboard
        if (user?.role === 'gh_supervisor' || user?.role === 'gh_leader') {
            return <Navigate to="/gh" replace />;
        }
        if (CREW_ROLES.includes(user?.role)) return <Navigate to="/mis-vuelos" replace />;
        return <Navigate to="/dashboard" replace />;
    }
    return <LoginPage />;
};

// All Routes
const AppRoutes = () => {
    return (
        <Routes>
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginWrapper />} />
            <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
            <Route path="/admin/tenants" element={<AdminRoute><AdminTenantsPage /></AdminRoute>} />
            <Route path="/admin/leads" element={<AdminRoute><AdminLeadsPage /></AdminRoute>} />
            <Route path="/admin/billing" element={<AdminRoute><AdminBillingPage /></AdminRoute>} />
            <Route path="/admin/billing/success" element={<AdminRoute><AdminBillingPage /></AdminRoute>} />
            <Route path="/admin/activity" element={<AdminRoute><AdminActivityPage /></AdminRoute>} />
            <Route path="/admin/staff" element={<AdminRoute><AdminStaffPage /></AdminRoute>} />
            <Route path="/admin/ai" element={<AdminRoute><AdminAIPage /></AdminRoute>} />
            <Route path="/admin/security" element={<AdminRoute><AdminSecurityPage /></AdminRoute>} />
            <Route path="/admin/errors" element={<AdminRoute><AdminErrorsPage /></AdminRoute>} />
            <Route path="/admin/support" element={<AdminRoute><AdminSupportPage /></AdminRoute>} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

            {/* Airline Routes */}
            <Route path="/login" element={<AirlineLoginWrapper />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage scope="tenant" />} />
            <Route path="/terminos" element={<LegalPage />} />
            <Route path="/privacidad" element={<LegalPage />} />
            <Route path="/cookies" element={<LegalPage />} />
            <Route path="/onboarding" element={<ProtectedRoute allowedRoles={['admin']}><OnboardingPage /></ProtectedRoute>} />
            <Route path="/admin/forgot-password" element={<ForgotPasswordPage scope="admin" />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/flights" element={<ProtectedRoute allowedRoles={['admin', 'dispatcher', 'ccod', 'comercial', 'counter', 'crew_admin', 'gerencia', 'analista']}><FlightsPage /></ProtectedRoute>} />
            <Route path="/schedule" element={<ProtectedRoute allowedRoles={['admin', 'comercial', 'ccod', 'gerencia', 'crew_admin']}><SchedulePage /></ProtectedRoute>} />
            <Route path="/fleet" element={<ProtectedRoute allowedRoles={['admin', 'maintenance', 'dispatcher', 'ccod', 'gerencia', 'analista']}><FleetPage /></ProtectedRoute>} />
            <Route path="/crew" element={<ProtectedRoute allowedRoles={['admin', 'crew_admin', 'ccod', 'gerencia']} requireModule="crew"><CrewPage /></ProtectedRoute>} />
            <Route path="/counter" element={<ProtectedRoute allowedRoles={['admin', 'counter', 'ccod']}><CounterPage /></ProtectedRoute>} />
            <Route path="/ventas" element={<ProtectedRoute allowedRoles={['admin', 'comercial', 'ventas', 'counter', 'gerencia']}><VentasPage /></ProtectedRoute>} />
            <Route path="/dispatch" element={<ProtectedRoute allowedRoles={['admin', 'dispatcher', 'ccod']}><DispatchPage /></ProtectedRoute>} />
            <Route path="/ramp" element={<ProtectedRoute allowedRoles={['admin', 'ramp', 'ccod', 'dispatcher']}><RampPage /></ProtectedRoute>} />
            <Route path="/mensajes" element={<ProtectedRoute><OpMessagesPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/mis-vuelos" element={<ProtectedRoute allowedRoles={['pilot', 'cabin_crew']}><MyFlightsPage /></ProtectedRoute>} />
            <Route path="/mi-bitacora" element={<ProtectedRoute allowedRoles={['pilot']}><MyLogbookPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/turnaround" element={<ProtectedRoute allowedRoles={['admin', 'ccod', 'dispatcher', 'ramp', 'counter', 'gerencia', 'maintenance', 'crew_admin']}><TurnaroundPage /></ProtectedRoute>} />
            <Route path="/turnaround/flight/:flightId" element={<ProtectedRoute allowedRoles={['admin', 'ccod', 'dispatcher', 'ramp', 'counter', 'gerencia', 'maintenance', 'crew_admin', 'pilot', 'cabin_crew']}><TurnaroundFlightPage /></ProtectedRoute>} />
            <Route path="/maintenance" element={<ProtectedRoute allowedRoles={['admin', 'maintenance']} requireModule="maintenance"><MaintenancePage /></ProtectedRoute>} />
            <Route path="/deferrals" element={<ProtectedRoute allowedRoles={['admin', 'maintenance', 'ccm', 'ccod', 'dispatcher']} requireModule="maintenance"><DeferralsPage /></ProtectedRoute>} />
            <Route path="/logbook" element={<ProtectedRoute allowedRoles={['admin', 'dispatcher', 'crew_admin', 'ccod']} requireModule="crew"><LogbookPage /></ProtectedRoute>} />
            <Route path="/statistics" element={<ProtectedRoute allowedRoles={['admin', 'dispatcher', 'ccod', 'gerencia', 'analista', 'comercial']}><StatisticsPage /></ProtectedRoute>} />
            <Route path="/cco" element={<ProtectedRoute allowedRoles={['admin', 'dispatcher', 'ccod', 'gerencia']}><CCODashboardPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute allowedRoles={['admin', 'maintenance', 'crew_admin', 'gerencia']}><DocumentsPage /></ProtectedRoute>} />
            <Route path="/crew-calendar" element={<ProtectedRoute allowedRoles={['admin', 'crew_admin', 'ccod', 'dispatcher', 'gerencia']} requireModule="crew"><CrewCalendarPage /></ProtectedRoute>} />
            <Route path="/sms" element={<ProtectedRoute allowedRoles={['admin', 'ccod', 'gerencia', 'analista', 'dispatcher', 'pilot', 'cabin_crew', 'maintenance', 'crew_admin', 'calidad']}><SMSPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'ccod', 'gerencia', 'analista', 'maintenance', 'crew_admin']}><ReportsPage /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute allowedRoles={['admin', 'gerencia']}><BillingPage /></ProtectedRoute>} />
            <Route path="/revenue" element={<ProtectedRoute allowedRoles={['admin', 'gerencia', 'analista', 'comercial']} requireModule="revenue"><RevenuePage /></ProtectedRoute>} />
            <Route path="/ai-agents" element={<ProtectedRoute allowedRoles={['admin', 'ccod', 'dispatcher', 'gerencia', 'analista', 'comercial', 'crew_admin', 'pilot', 'maintenance', 'ccm']} requireModule="ai_agents"><AIAgentsPage /></ProtectedRoute>} />
            <Route path="/predictive-mx" element={<ProtectedRoute allowedRoles={['admin', 'maintenance', 'ccod', 'dispatcher', 'gerencia']} requireModule="predictive_maintenance"><PredictiveMaintenancePage /></ProtectedRoute>} />
            <Route path="/gh" element={<ProtectedRoute allowedRoles={['admin', 'gh_supervisor', 'gh_leader']}><GHDashboardPage /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute allowedRoles={['admin', 'gh_supervisor']}><SupportPage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']}><UsersPage /></ProtectedRoute>} />

            {/* Landing */}
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <AdminAuthProvider>
                    <BrowserRouter>
                        <AppRoutes />
                        <CookieBanner />
                        <Toaster position="top-right" richColors />
                    </BrowserRouter>
                </AdminAuthProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
